import { CalendarDays, CheckCircle2, MapPin, Ticket } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { api, type PublicRegistration } from "../api.js";
import { apiErrorMessage } from "../../api/client.js";
import { BrandMark } from "../../design/components/BrandMark.js";
import { Button } from "../../design/components/Button.js";
import { Alert, Skeleton } from "../../design/components/Feedback.js";
import { formatDateRange } from "../formatters.js";

function money(amountMinor: number, currency: string) {
  return new Intl.NumberFormat(undefined, { style: "currency", currency }).format(amountMinor / 100);
}

export function PublicRegistrationPage() {
  const { organizationId = "", publicSlug = "" } = useParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<PublicRegistration | null>(null);
  const [categoryId, setCategoryId] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [affiliation, setAffiliation] = useState("");
  const [accepted, setAccepted] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const idempotencyKey = useRef(crypto.randomUUID());

  useEffect(() => {
    let active = true;
    setError("");
    void api.publicRegistration(organizationId, publicSlug)
      .then((result) => {
        if (!active) return;
        setEntry(result);
        setCategoryId(result.categories[0]?.id ?? "");
      })
      .catch((reason) => { if (active) setError(apiErrorMessage(reason, "Registration could not be loaded.")); });
    return () => { active = false; };
  }, [organizationId, publicSlug]);

  const submit = async () => {
    if (!entry || !categoryId || !displayName.trim() || !accepted) return;
    setBusy(true);
    setError("");
    try {
      await api.registerForConference(entry.conference.id, entry.conference.organization_id, {
        category_id: categoryId,
        standard_answers: { display_name: displayName.trim(), affiliation: affiliation.trim() },
        restricted_answers: {},
        declarations: ["registration-terms-v1"],
      }, idempotencyKey.current);
      navigate(`/my/conferences/${entry.conference.id}/registration`, { replace: true });
    } catch (reason) {
      setError(apiErrorMessage(reason, "Your registration could not be completed. Your information is still here; please try again."));
    } finally {
      setBusy(false);
    }
  };

  return <main className="public-registration-page">
    <section className="public-registration-card">
      <header className="public-registration-brand"><BrandMark /><span>Attendee registration</span></header>
      {error ? <Alert tone="danger">{error}</Alert> : null}
      {!entry && !error ? <div className="public-registration-loading"><Skeleton /><Skeleton /><Skeleton /></div> : null}
      {entry ? <>
        <div className="public-registration-heading">
          <p>{entry.conference.short_name || "Conference registration"}</p>
          <h1>Register for {entry.conference.name}</h1>
          <div>
            {entry.conference.starts_on ? <span><CalendarDays size={16} aria-hidden="true" />{formatDateRange(entry.conference.starts_on, entry.conference.ends_on)}</span> : null}
            {entry.conference.venue ? <span><MapPin size={16} aria-hidden="true" />{entry.conference.venue}</span> : null}
          </div>
        </div>
        {!entry.categories.length ? <Alert tone="neutral"><strong>Registration is not open yet</strong><br />The organizing team has not opened an attendee category. Return to the conference website for updates.</Alert> : <form onSubmit={(event) => { event.preventDefault(); void submit(); }} className="public-registration-form">
          <fieldset className="registration-category-options">
            <legend>Choose your registration</legend>
            {entry.categories.map((category) => <label key={category.id} className={categoryId === category.id ? "is-selected" : ""}>
              <input type="radio" name="registration-category" value={category.id} checked={categoryId === category.id} onChange={() => setCategoryId(category.id)} />
              <span><Ticket aria-hidden="true" size={18} /><strong>{category.name}</strong><small>{category.places_remaining === null ? "No capacity limit" : category.places_remaining > 0 ? `${category.places_remaining} places remaining` : category.waitlist_enabled ? "Join the waitlist" : "Full"}</small></span>
              <b>{money(category.amount_minor, category.currency)}</b>
            </label>)}
          </fieldset>
          <div className="public-registration-fields">
            <label>Name for your badge<input required value={displayName} onChange={(event) => setDisplayName(event.target.value)} autoComplete="name" /></label>
            <label>Affiliation<input value={affiliation} onChange={(event) => setAffiliation(event.target.value)} autoComplete="organization" /></label>
          </div>
          <label className="public-registration-consent"><input type="checkbox" checked={accepted} onChange={(event) => setAccepted(event.target.checked)} /><span><CheckCircle2 aria-hidden="true" size={18} />I confirm these details and accept the conference registration terms.</span></label>
          <Button type="submit" busy={busy} disabled={!categoryId || !displayName.trim() || !accepted}>Complete registration</Button>
          <p className="public-registration-note">A confirmation will be sent to your verified account email. Online card payment is not collected here; any payment instructions come from the organizer.</p>
        </form>}
      </> : null}
    </section>
  </main>;
}
